<template>
  <SidebarShell :model="model" />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ItemType, type Item } from '@/shared/graphql/legacy/generated'
import { isItemInactive } from '@/features/search/utils'
import { formatSearchTotal } from '@/features/search/formatSearchTotal'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { useDevice } from '@/shared/composables/useDevice'
import { usePlatformStore } from '@/shared/stores/platform'
import SidebarShell from '@/features/explorer/components/sidebar/SidebarShell.vue'
import type {
  SidebarModel,
  SidebarRow,
} from '@/features/explorer/components/sidebar/types'

const props = defineProps<{
  selectedItem?: Item
  total?: number | null
}>()

const router = useRouter()
const device = useDevice()
const platformStore = usePlatformStore()

if (device.isTabletHorizontal.value) {
  platformStore.setSidebarCollapse(true)
}

watch(
  () => props.selectedItem,
  () => {
    if (
      document.body.style.pointerEvents === 'none' ||
      device.isDesktop.value
    ) {
      return
    }
    platformStore.setSidebarCollapse(!props.selectedItem)
  }
)

const previewSrc = (url?: string | null) =>
  url ? applyAuthorizationToken(url, [{ key: 'width', value: 300 }]) : null

const breadcrumbPath = (parentPath?: string | null) => {
  const segments = (parentPath ?? '/').split('/').filter(Boolean)
  return ['Dacr', ...segments]
}

const navigatePath = (path: string[]) => {
  if (path.length <= 1) {
    router.push('/home')
  } else {
    router.push(`/home/${path.slice(1).join('/')}`)
  }
}

const model = computed<SidebarModel | null>(() => {
  const item = props.selectedItem

  if (item && item.type === ItemType.File && item.file) {
    const file = item.file
    const inactive = isItemInactive(item)
    const rows: SidebarRow[] = [
      {
        kind: 'breadcrumb',
        label: 'Located in',
        path: breadcrumbPath(item.parentPath),
        onNavigate: navigatePath,
      },
      inactive
        ? { kind: 'text', label: 'Timestamp', value: '—' }
        : {
            kind: 'registrations',
            label: 'Timestamp',
            items: file.registrations.map((registration, index) => ({
              id: registration.id ?? String(index),
              date: formatTimestamp(registration.timestamp) ?? '',
              flagUrl: registration.icon,
              certificateUrl: registration.certificateUrl,
            })),
          },
      { kind: 'text', label: 'Owner', value: file.owner?.name ?? '—' },
      { kind: 'text', label: 'Size', value: file.size || '—' },
      { kind: 'text', label: 'Type', value: file.friendlyType || file.type },
      {
        kind: 'text',
        label: 'Visibility',
        value: inactive ? '—' : file.isPrivate ? 'Private' : 'Public',
      },
      {
        kind: 'text',
        label: 'Copyright protection expiration',
        value: inactive
          ? '—'
          : file.billingExpirationDate
            ? (formatTimestamp(file.billingExpirationDate) ?? 'Indefinite')
            : 'Indefinite',
      },
      {
        kind: 'link',
        label: 'Edit copyright settings',
        onClick: () => router.push(`/file/${file.copyrightId}`),
      },
    ]

    return {
      title: item.name,
      share: null,
      thumbnail: {
        type: fileTypeToThumbnail(file.type),
        src: previewSrc(file.imageUrl),
        onClick: inactive
          ? undefined
          : () => router.push(`/file/${file.copyrightId}`),
      },
      rows,
    }
  }

  if (item && item.type === ItemType.Folder) {
    return {
      title: item.name,
      share: null,
      thumbnail: { type: 'folder' },
      rows: [
        {
          kind: 'breadcrumb',
          label: 'Located in',
          path: breadcrumbPath(item.parentPath),
          onNavigate: navigatePath,
        },
        {
          kind: 'text',
          label: 'Number of registrations',
          value: String(item.numberOfItems ?? 0),
        },
      ],
    }
  }

  if (props.total === undefined || props.total === null) return null

  return {
    title: 'Your registrations',
    share: null,
    thumbnail: { type: 'folder' },
    rows: [
      {
        kind: 'text',
        label: 'Number of results',
        value: formatSearchTotal(props.total),
      },
    ],
  }
})
</script>
